import {
  RequiredIndicatorPlacement as Placement,
  RequiredIndicator,
} from '@wix/ambassador-forms-v4-form/types';
import React, { type ReactNode } from 'react';

import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { useTranslation } from '../../contexts/translation-context';
import { findChildOfType } from '../../services/find-child';

const HIDDEN_LABEL_STYLE = {
  visibility: 'hidden' as const,
  display: 'block',
  height: 0,
  margin: 0,
  padding: 0,
};

const ZERO_WIDTH_SPACE = '\u200b';

export type LabelProps = {
  children: ReactNode;
  className?: string;
};

export type LabelInternalProps = {
  as?: 'label' | 'div';
  requiredIndicatorHidden?: boolean;
  label?: string;
};

export const Label = ({ children, className, ...internal }: LabelProps) => {
  const {
    as = 'label',
    requiredIndicatorHidden = false,
    label: internalLabel,
  } = internal as LabelInternalProps;
  const {
    label: propsLabel,
    required = false,
    hideLabel = false,
    requiredIndicatorType = RequiredIndicator.ASTERISK,
    requiredIndicatorPlacement = Placement.AFTER_FIELD_TITLE,
  } = useFieldPropsV2();

  const { labelId, inputId } = useFieldAttributes();

  const Tag = as;

  const requiredElement =
    required && !requiredIndicatorHidden
      ? findChildOfType(children, Label.Required)
      : null;

  const labelAfterRequiredIndicator =
    requiredIndicatorType === RequiredIndicator.ASTERISK &&
    requiredIndicatorPlacement === Placement.BEFORE_FIELD_TITLE;

  const label = internalLabel ?? propsLabel;
  const labelContent = label || ZERO_WIDTH_SPACE;

  return (
    <Tag
      id={labelId}
      htmlFor={as === 'label' ? inputId : undefined}
      className={className}
      style={hideLabel ? HIDDEN_LABEL_STYLE : undefined}
    >
      {labelAfterRequiredIndicator ? (
        <>
          {requiredElement}
          {labelContent}
        </>
      ) : (
        <>
          {labelContent}
          {requiredElement}
        </>
      )}
    </Tag>
  );
};

type RequiredProps = {
  className?: string;
};

const Required = ({ className }: RequiredProps) => {
  const { t } = useTranslation();
  const { requiredIndicatorType } = useFieldPropsV2();

  if (requiredIndicatorType === RequiredIndicator.TEXT) {
    return (
      <span className={className} aria-hidden="true">
        {t('settings.required-indicator-text')}
      </span>
    );
  }

  return (
    <span className={className} aria-hidden="true">
      *
    </span>
  );
};

Label.Required = Required;
